// src/components/games/crossroads/CardDetailModal.tsx
// Modal for viewing card details with zoom and play option

import React from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonIcon,
  IonBadge,
  IonText,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { Card, CardType, CreatureCard, ArtCard, ManaSourceCard, ElementType, CreatureAbility } from './types';
import { CardDisplay } from './CardDisplay';
import './CardDetailModal.css';

interface CardDetailModalProps {
  card: Card | null;
  isOpen: boolean;
  onDismiss: () => void;
  onPlay?: () => void;
  canPlay?: boolean;
  showPlayButton?: boolean;
  onTap?: () => void;
  canTap?: boolean;
  showTapButton?: boolean;
  copiedAbilities?: CreatureAbility[];
}

export const CardDetailModal: React.FC<CardDetailModalProps> = ({
  card,
  isOpen,
  onDismiss,
  onPlay,
  canPlay = true,
  showPlayButton = true,
  onTap,
  canTap = true,
  showTapButton = false,
  copiedAbilities,
}) => {
  if (!card) return null;

  const isCreature = (c: Card): c is CreatureCard => c.type === CardType.CREATURE;
  const isArt = (c: Card): c is ArtCard => c.type === CardType.ART;
  const isManaSource = (c: Card): c is ManaSourceCard => c.type === CardType.MANA_SOURCE;

  const handlePlayCard = () => {
    if (onPlay && canPlay) {
      onPlay();
      onDismiss();
    }
  };

  const handleTapCard = () => {
    if (onTap && canTap) {
      onTap();
      onDismiss();
    }
  };

  const getElementIcon = (element: ElementType): string => {
    const icons: Record<ElementType, string> = {
      [ElementType.FIRE]: '🔥',
      [ElementType.WATER]: '💧',
      [ElementType.EARTH]: '🌱',
      [ElementType.DARK]: '🌑',
      [ElementType.LIGHT]: '☀️',
      [ElementType.FAE]: '✨',
      [ElementType.WIND]: '💨',
      [ElementType.NEUTRAL]: '⚪',
    };
    return icons[element] || '⚪';
  };

  const renderCost = () => {
    if (!('cost' in card) || !card.cost || Object.keys(card.cost).length === 0) {
      return <IonBadge color="success">Free</IonBadge>;
    }

    return (
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {Object.entries(card.cost).map(([element, amount]) => (
          <IonBadge key={element} color="primary">
            {getElementIcon(element as ElementType)} {amount}
          </IonBadge>
        ))}
      </div>
    );
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss} className="card-detail-modal">
      <IonHeader>
        <IonToolbar>
          <IonTitle>{card.name}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="card-detail-content">
          {/* Card Image */}
          <div className="card-detail-image">
            <CardDisplay card={card} size="large" showDetails={true} />
          </div>

          {/* Card Info */}
          <div className="card-detail-info">
            <div className="card-detail-section">
              <IonText color="medium">
                <h3>Type</h3>
              </IonText>
              <IonBadge color="secondary">{card.type.toUpperCase()}</IonBadge>
            </div>

            <div className="card-detail-section">
              <IonText color="medium">
                <h3>Cost</h3>
              </IonText>
              {renderCost()}
            </div>

            {isCreature(card) && (
              <>
                <div className="card-detail-section">
                  <IonText color="medium">
                    <h3>Stats</h3>
                  </IonText>
                  <IonBadge color="danger">{card.power} / {card.health}</IonBadge>
                </div>

                {card.keywords && card.keywords.length > 0 && (
                  <div className="card-detail-section">
                    <IonText color="medium">
                      <h3>Keywords</h3>
                    </IonText>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {card.keywords.map((keyword, idx) => (
                        <IonBadge key={idx} color="tertiary">
                          {keyword}
                        </IonBadge>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {isManaSource(card) && (
              <div className="card-detail-section">
                <IonText color="medium">
                  <h3>Provides</h3>
                </IonText>
                <IonBadge color="primary">
                  {getElementIcon(card.elementType)} {card.elementType.toUpperCase()}
                </IonBadge>
              </div>
            )}

            {isArt(card) && (
              <div className="card-detail-section">
                <IonText color="medium">
                  <h3>Effect</h3>
                </IonText>
                <IonText>
                  <p>{card.flavorText || 'This card has a special effect.'}</p>
                </IonText>
              </div>
            )}

            {card.flavorText && (
              <div className="card-detail-section">
                <IonText color="medium">
                  <h3>Flavor Text</h3>
                </IonText>
                <IonText className="flavor-text">
                  <p style={{ fontStyle: 'italic' }}>"{card.flavorText}"</p>
                </IonText>
              </div>
            )}

            {/* Show copied abilities (e.g., from Mire Druid's copy ability) */}
            {copiedAbilities && copiedAbilities.length > 0 && (
              <div className="card-detail-section">
                <IonText color="medium">
                  <h3>Copied Abilities</h3>
                </IonText>
                {copiedAbilities.map((ability, idx) => (
                  <IonText key={idx}>
                    <p style={{ marginTop: '8px', fontSize: '14px' }}>
                      <strong>{ability.tapCost ? '⟳ TAP: ' : ''}</strong>
                      {ability.description}
                    </p>
                  </IonText>
                ))}
              </div>
            )}
          </div>

          {/* Play Button */}
          {showPlayButton && (
            <div className="card-detail-actions">
              <IonButton
                expand="block"
                size="large"
                onClick={handlePlayCard}
                disabled={!canPlay}
                color={canPlay ? 'primary' : 'medium'}
              >
                {canPlay ? 'Play Card' : 'Cannot Play'}
              </IonButton>
            </div>
          )}

          {/* Tap Button (for mana sources and creature abilities) */}
          {showTapButton && (
            <div className="card-detail-actions">
              <IonButton
                expand="block"
                size="large"
                onClick={handleTapCard}
                disabled={!canTap}
                color={canTap ? 'success' : 'medium'}
              >
                {canTap ? (
                  copiedAbilities && copiedAbilities.length > 0
                    ? 'Tap to Activate Ability'
                    : 'Tap for Mana'
                ) : 'Already Tapped'}
              </IonButton>
            </div>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};

export default CardDetailModal;
